import locationLogic from '../../utilities/locationLogic';
import cfiLogic from '../../utilities/cfiLogic';
import Book from '../Book';
import BookPage from '../BookPage';
import BookMarker from '../BookMarker';
import BookDocument from '../BookDocument';

/**
 * The idea with Location is to have a simple model and API for tracking book locations,
 * this way when you need to "go to" a place in the book, for example a particular page, you can use this abstraction instead of
 * creating logic for each different way of accessing a location (be it page, document, cfi, etc).
 *
 * To go to a particular cfi, the code could look like: goToLocation(new BookLocation({ page: book.pages[12] }))
 * and the model will figure out what document and element to target.
 *
 * This also allows a list of locations, e.g. something like locationHistoryStack: [] to include a mix of location target types,
 * some that target page, others just a document, some that point to specific cfis, etc.
 */

class BookLocation {
  /**
   * When a location is initialized, it may refer to content on a document from the book that is
   * different than the document currently rendered in the iframe.
   *
   * However, when the reader is navigated to the BookLocation, the content in the frame becomes a reference
   * for the BookLocation's properties. It can now derive some missing properties from existing properties.
   *
   * For instance, if it has a page but no cfi, it can determine the cfi based on where the page starts.
   * Or, if it has a cfi but no page, it can determine the page where the location starts.
   * Similarly, it can determine cfi from element, cfi from percentageOfDocument, and similar associations.
   */
  #resolved = false;

  /**
   * The document is the primary property of a BookLocation, determining which XHTML document of an ePub or
   * PDF that was converted into an ePub. A BookLocation can be created with a BookDocument alone.
   *
   * If only an index or number (index + 1) is provided, a Book reference is required.
   *
   * If no other specificity is provided, the CFI will be set to '/4/2', indicating the start of a document.
   */
  #document;

  /**
   * Similar to the document property, BookMarker and BookPage objects can point to a specific place in a title
   * by themselves. They contain a BookDocument reference and can also include a CFI or element property to point
   * to a specific place in the document.
   *
   * These can be considered "landmarks" in the book.
   *
   * The BookLocation may point to an exact place in a book where a landmark resides, or it can refer to the page
   * or marker in which the location resides. In other words, by having a page or marker property set, the
   * BookLocation indicates that it resides somewhere between the start of the page or marker and before the start
   * of the next page or marker.
   *
   * If no explicit CFI (or element, or percentageOfDocument, or positionOfBook) is set before the marker or page
   * properties are set, the BookLocation will derive a CFI (and other position properties) from the marker or page.
   *
   * If no explicit marker or page is set before CFI (or other document position property), both will be determined
   * by calculating the nearest marker or page that occurs before the CFI.
   *
   * It is possible, by passing both a landmark as well as a document position property (e.g. CFI), to create a
   * BookLocation where the CFI exists outside of the range of the landmark (before it starts, or after a new one
   * begins). The BookLocation does not necessarily have the information needed to determine whether that is the
   * case when it is initialized. So it is allowed, but not recommended.
   * This could happen if: a) a location is constructed carelessly or b) bad data is present in the database.
   * It would likely lead to unexpected, but not catastrophically bad, behavior.
   *
   * It is also possible for the page and marker properties to be similarly out of sync for the same reason.
   * Given any ambiguity, CFI should reign supreme and page should take precedence over marker.
   */
  #marker;
  #page;

  /**
   * As a class, cfi, element, percentageOfBook, and percentageOfDocument can be considered "document position"
   * properties, but they all resolve to a CFI in the end. The CFI property can actually specify a range.
   *
   * By themselves, these only specify a position in any BookDocument.
   *
   * So CFI, element, and percentageOfDocument must be paired with a BookDocument passed into the contructor,
   * either directly or through a BookPage or BookMarker.
   */
  #cfi;
  #element;
  #percentageOfDocument;
  #wordOffset;

  /**
   * The event that triggered the creation of this Location object.
   * e.g. DocumentScrolledEvent
   */
  event;

  constructor({
    // Book
    book,
    // Document
    documentNumber,
    documentIndex,
    document,
    // Landmarks
    marker,
    markerIndex,
    page,
    pageIndex,
    // Document Position
    cfi,
    element,
    percentageOfDocument,
    percentageOfBook,
    wordOffset,
    // Event
    event,
  }) {
    /**
     * Input normalization and type checking.
     * Ensure we aren't dealing with any unexpected values before continuing.
     */

    // Ensure book is a Book or cast to undefined.
    book = book ?? document?.book ?? page?.book ?? marker?.book ?? undefined;
    if (book && !(book instanceof Book)) {
      throw new Error('book must be an instance of Book');
    }

    // Cast documentNumber and documentIndex to either an integer or NaN.
    documentNumber = parseInt(documentNumber);
    documentIndex = parseInt(documentIndex);

    // Ensure document is a BookDocument or cast to undefined.
    document = document ?? undefined;
    if (document && !(document instanceof BookDocument)) {
      throw new Error('document must be an instance of BookDocument', document);
    }
    this.#document = document;

    // Ensure marker is a BookMarker or cast to undefined.
    marker = marker ?? book.markers[markerIndex] ?? undefined;
    if (marker && !(marker instanceof BookMarker)) {
      throw new Error('marker must be an instance of BookMarker', marker);
    }
    this.#marker = marker;

    // Ensure page is a BookPage or cast to undefined.
    page = page ?? book.pages[pageIndex] ?? undefined;
    if (page && !(page instanceof BookPage)) {
      throw new Error('page must be an instance of BookPage', page);
    }
    this.#page = page;

    // Ensure cfi is a valid cfi or undefined.
    cfi = cfi ?? undefined;
    if (cfi && !cfiLogic.isValidCfi(cfi)) {
      console.warn('cfi is not a valid cfi', cfi);
      cfi = undefined;
    }

    // Ensure element is a string or undefined.
    // At this point we are unable to verify that the element exists,
    // because the element may be within a Document that is not currently rendered in the DOM.
    element = typeof element === 'string' ? element : undefined;

    // Cast percentageOfDocument and percentageOfBook to either a float or NaN
    percentageOfDocument = parseFloat(percentageOfDocument);
    percentageOfBook = parseFloat(percentageOfBook);

    this.#wordOffset = wordOffset ?? undefined;

    /**
     * Set the event property
     */

    this.event = event;

    /**
     * Set document position properties.
     */

    if (
      Number(Boolean(cfi)) +
        Number(Boolean(element)) +
        Number(!isNaN(percentageOfDocument)) +
        Number(!isNaN(percentageOfBook)) >
      1
    ) {
      console.warn(
        `More than one document position property was passed as an argument to BookLocation. Only one will be used.`,
        cfi,
        element,
        percentageOfDocument,
        percentageOfBook,
      );
    }

    if (isNaN(percentageOfBook)) {
      // percentageOfBook takes precedence over all other document position properties if it is passed in.
      if (cfi) {
        this.cfi = cfi;
      } else if (element) {
        this.element = element;
      } else if (!isNaN(percentageOfDocument)) {
        this.percentageOfDocument = percentageOfDocument;
      }
    }

    /**
     * If no Document is set at this point, try to set it or throw an error.
     */

    if (!this.document) {
      if (!book) {
        throw new Error(
          'BookLocation must be instantiated with a Book, BookDocument, BookPage, or BookMarker object.',
        );
      }

      if (documentIndex) {
        this.#document = book.documents[documentIndex];
      } else if (documentNumber) {
        this.#document = book.documents[documentNumber - 1];
      } else if (percentageOfBook === 1) {
        // if percentageOfBook is 1, that's the end of last document.
        this.#document = book.documents[book.documents.length - 1];
        this.percentageOfDocument = 1;
      } else if (percentageOfBook) {
        // Compute document from percentageOfBook, ONLY if no document was computed up to this point.
        // if not the end of book, compute the document and percentageOfdocument based of percentageOfBook
        for (let document of book.documents) {
          if (
            document.percentageStart <= percentageOfBook &&
            percentageOfBook < document.percentageEnd
          ) {
            // The document is whatever document the percentageOfBook lands on.
            this.#document = document;

            // Only save the percentage of the document on the BookLocation, not the percentage of the book.
            const numerator = percentageOfBook - document.percentageStart;
            const denominator =
              document.percentageEnd - document.percentageStart;
            this.percentageOfDocument =
              denominator !== 0 ? numerator / denominator : 0;
          }
        }
      } else if (this.marker) {
        // If the marker is set but the document is not, use the marker's document as the document.
        this.#document = this.marker.document;
      } else if (this.page) {
        // If the page is set but the document is not, use the page's document as the document.
        this.#document = this.page.document;
      } else {
        // Fall back on the first document in the book.
        this.#document = book.documents[0];
      }
    }
  }

  swapBook(newBook) {
    return new BookLocation({
      book: newBook,
      documentIndex: this.documentIndex,
      markerIndex: this.markerIndex,
      pageIndex: this.pageIndex,
      cfi: this.cfi,
      element: this.element,
      event: this.event,
    });
  }

  /**
   * Getter and setter for the CFI.
   */

  get cfi() {
    if (this.#cfi) {
      return this.#cfi;
    }

    if (this.page?.cfi) {
      return this.page.cfi;
    }

    if (this.marker?.cfi) {
      return this.marker.cfi;
    }

    return undefined;
  }

  set cfi(value) {
    if (this.cfi) {
      console.warn('Tried to set cfi, but a cfi was already available.', this);

      return;
    }

    this.#cfi = value;
  }

  /**
   * Getter and setter for the element.
   */

  get element() {
    if (this.#element) {
      return this.#element;
    }

    if (this.page?.element) {
      return this.page.element;
    }

    if (this.marker?.element) {
      return this.marker.element;
    }

    return undefined;
  }

  set element(newElement) {
    if (this.element) {
      console.warn(
        'Tried to set element, but an element was already available.',
        this,
      );

      return;
    }

    this.#element = newElement;
  }

  /**
   * Getter and setter for percentageOfDocument.
   */

  get percentageOfDocument() {
    return this.#percentageOfDocument;
  }

  set percentageOfDocument(value) {
    if (this.percentageOfDocument !== undefined) {
      console.warn(
        'Tried to set percentageOfDocument, but percentageOfDocument was already set.',
        this,
      );

      return;
    }

    this.#percentageOfDocument = value;
  }

  /**
   * Getter to compute percentageOfBook.
   */

  get percentageOfBook() {
    if (isNaN(parseFloat(this.percentageOfDocument))) {
      return undefined;
    }

    return (
      this.document.percentageStart +
      this.percentageOfDocument *
        (this.document.percentageEnd - this.document.percentageStart)
    );
  }

  get wordOffset() {
    return this.#wordOffset;
  }

  /**
   * Getter for the document.
   */

  get document() {
    return this.#document;
  }

  get documentIndex() {
    return this.document?.index;
  }

  /**
   * Getter and setter for the page.
   */

  get page() {
    return this.#page;
  }

  set page(newPage) {
    if (this.page) {
      console.warn(
        'Tried to set a page, but a page was already available.',
        this,
      );

      return;
    }

    if (!newPage) {
      return;
    }

    if (newPage instanceof BookPage) {
      this.#page = newPage;
      return;
    }

    throw new Error('page must be an instance of BookPage', newPage);
  }

  get pageIndex() {
    return this.page?.index;
  }

  /**
   * Getter and setter for the marker.
   */

  get marker() {
    return this.#marker;
  }

  set marker(newMarker) {
    if (this.marker !== undefined) {
      console.warn(
        'Tried to set a marker, but a marker was already available.',
        this,
      );

      return;
    }

    if (!newMarker) {
      return;
    }

    if (newMarker instanceof BookMarker) {
      this.#marker = newMarker;
      return;
    }

    throw new Error('marker must be an instance of Bookmarker');
  }

  get markerIndex() {
    return this.marker?.index;
  }

  get topLevelMarker() {
    return this.marker?.topLevelMarker;
  }

  /**
   * Getter for the book.
   */

  get book() {
    return this.document?.book;
  }

  /**
   * Getter to generate an href for the location.
   *
   * Note: this shaves off extra query params.
   * To get an href while including other query params, use locationLogic.generatePath(location, searchParamsObject)
   */

  get href() {
    return locationLogic.generatePath(this);
  }

  /**
   * Getter to convert current Location into more readable form.
   */
  get readableLocation() {
    if (this.#marker && this.#page) {
      return `${this.marker.label}, Page: ${this.page.label}`;
    }

    if (this.#marker) {
      return `${this.marker.label}`;
    }

    if (this.#page) {
      return `Page: ${this.page?.label}`;
    }

    if (this.#document) {
      return `Part of document ${this.document.label}`;
    }

    throw new Error('No proper bookLocation');
  }

  /**
   * Getter for whether the location is resolved and a function to resolve it.
   *
   * Resolving is the process of using existing properties, with an iframe for reference,
   * to derive remaining properties.
   */

  get resolved() {
    return this.#resolved;
  }

  resolve(frame) {
    if (this.#resolved || !frame?.contentDocument) {
      return;
    }

    this.#resolveDocumentPosition(frame);
    this.#resolveLandmarks(frame);

    this.#resolved = true;
  }

  #resolveDocumentPosition(frame) {
    // If there is a cfi, use it to resolve the percentageOfDocument and element.
    if (this.cfi) {
      const elementObject = locationLogic.getElementForCfi(frame, this.cfi);

      if (elementObject) {
        this.percentageOfDocument =
          locationLogic.getPercentageOfDocumentForElement(frame, elementObject);

        if (elementObject?.id) {
          this.element = elementObject.id;
        }

        return;
      }

      console.warn(
        `Failed to find DOM element #${this.element} for BookLocation using cfi string.`,
      );
    }

    // If there is a percentageOfDocument, use it to resolve the CFI and element.
    if (this.percentageOfDocument) {
      const pixels = locationLogic.getPixelsForPercentageOfDocument(
        frame,
        this.percentageOfDocument,
      );

      this.cfi = locationLogic.getCfiForPixelOffset(frame, pixels);

      const elementObject = locationLogic.getElementForCfi(frame, this.cfi);

      if (elementObject?.id) {
        this.element = elementObject.id;
      }

      return;
    }

    // If there is an element, use it to resolve the percentageOfDocument and CFI.
    if (this.element && this.element !== '') {
      const elementObject = frame.contentDocument.getElementById(this.element);

      if (elementObject) {
        this.percentageOfDocument =
          locationLogic.getPercentageOfDocumentForElement(frame, elementObject);

        // For blacklisted elements (like .rs-auto-pagebreak) or empty elements (like <hr/>),
        // use the next sibling with text content for CFI generation
        // because CFI library produces invalid CFIs for such elements.
        let cfiTargetElement = elementObject;
        const elementIsBlacklisted = cfiLogic.isBlacklisted(elementObject);
        const elementHasTextContent =
          elementObject.textContent &&
          elementObject.textContent.trim().length > 0;

        if (elementIsBlacklisted || !elementHasTextContent) {
          let sibling = elementObject.nextElementSibling;
          while (sibling) {
            const isBlacklisted = cfiLogic.isBlacklisted(sibling);
            const hasTextContent =
              sibling.textContent && sibling.textContent.trim().length > 0;
            if (!isBlacklisted && hasTextContent) {
              break;
            }
            sibling = sibling.nextElementSibling;
          }
          if (sibling) {
            cfiTargetElement = sibling;
          }
        }

        this.cfi = cfiLogic.getCfiForElement(
          frame.contentDocument,
          cfiTargetElement,
        );

        return;
      }

      console.warn(
        `Failed to find DOM element #${this.element} for BookLocation using element string.`,
      );
    }

    // Default to the beginning of the document if there is no position information.
    this.cfi = '/4/2';
    const elementObject = locationLogic.getElementForCfi(frame, this.cfi);

    if (elementObject) {
      this.percentageOfDocument =
        locationLogic.getPercentageOfDocumentForElement(frame, elementObject);
    } else {
      this.percentageOfDocument = 0;
    }
  }

  #resolveLandmarks(frame) {
    this.page = locationLogic.getPageForPercentageOfDocument(
      frame,
      this.document,
      this.percentageOfDocument,
    );

    this.marker = locationLogic.getMarkerForPercentageOfDocument(
      frame,
      this.document,
      this.percentageOfDocument,
    );
  }
}

export default BookLocation;
